import type { FetchKey } from '@app/src/api/fetchHooks'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterFacetSelect from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import OptionWithLabel from '@app/src/pages/ResourceCollection/Filters/OptionWithLabel'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { SortOrder } from '@app/src/types/filter'
import { Chip } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'

interface ProductMappingFiltersProps {
  fetchKey: FetchKey
  endpoint: string
  filterNamePrefix: string
  implicitFilters?: FilterGroup[]
}

const ProductMappingFilters: React.FC<ProductMappingFiltersProps> = ({
  fetchKey,
  endpoint,
  filterNamePrefix,
  implicitFilters,
}) => {
  const { formatMessage } = useIntl()

  return (
    <FilterSection defaultCollapsed label={formatMessage({ id: 'navbar.productMapping' })}>
      <Filter operator={Operators.In} name={`${filterNamePrefix}.actorType.id`}>
        {({ value, onChange, filterName }) => (
          <FilterFacetSelect
            implicitFilters={implicitFilters}
            size="small"
            multiple
            facetsParam={{
              key: [fetchKey, filterName],
              endpoint: endpoint,
              facetsParam: [{ name: `${filterNamePrefix}.actorType.name` }],
            }}
            renderOption={(props, option, { selected }) => (
              <OptionWithLabel
                key={option.label}
                props={props}
                label={formatMessage({
                  id: option?.label ? `schemas.mappingNode.actorTypeValues.${option.label}` : 'general.notAvailable',
                })}
                multiple
                selected={selected}
              />
            )}
            renderTags={(value, getTagProps) =>
              value?.map(
                (option, index) =>
                  option && (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.value?.toString() || index}
                      label={formatMessage({
                        id: option?.label
                          ? `schemas.mappingNode.actorTypeValues.${option.label}`
                          : 'general.notAvailable',
                      })}
                      size="small"
                    />
                  ),
              )
            }
            filterName={filterName}
            onChange={value => onChange(value)}
            fieldLabel={formatMessage({ id: 'schemas.mappingNode.actorType' })}
            value={[value].flat()}
          />
        )}
      </Filter>
      <Filter operator={Operators.In} name={`${filterNamePrefix}.tier`}>
        {({ value, onChange, filterName }) => (
          <FilterFacetSelect
            implicitFilters={implicitFilters}
            size="small"
            multiple
            facetsParam={{
              key: [fetchKey, filterName],
              endpoint: endpoint,
              facetsParam: [{ name: `${filterNamePrefix}.tier`, isEnum: true }],
              sort: { target: 'label', order: SortOrder.ASCENDING },
            }}
            filterName={filterName}
            onChange={value => onChange(value)}
            fieldLabel={formatMessage({ id: 'schemas.mappingNode.tier' })}
            value={[value].flat()}
          />
        )}
      </Filter>
      <Filter operator={Operators.In} name={`${filterNamePrefix}.product.productStatus`}>
        {({ value, onChange, filterName }) => (
          <FilterFacetSelect
            implicitFilters={implicitFilters}
            size="small"
            multiple
            facetsParam={{
              key: [fetchKey, filterName],
              endpoint: endpoint,
              facetsParam: [{ name: `${filterNamePrefix}.product.productStatus`, isEnum: true }],
            }}
            renderOption={(props, option, { selected }) => (
              <OptionWithLabel
                key={option.label}
                props={props}
                label={formatMessage({
                  id: option?.label ? `schemas.product.productStatusValues.${option.label}` : 'general.notAvailable',
                })}
                multiple
                selected={selected}
              />
            )}
            renderTags={(value, getTagProps) =>
              value?.map(
                (option, index) =>
                  option && (
                    <Chip
                      {...getTagProps({ index })}
                      key={option?.value?.toString() || index}
                      label={formatMessage({
                        id: option?.label
                          ? `schemas.product.productStatusValues.${option.label}`
                          : 'general.notAvailable',
                      })}
                      size="small"
                    />
                  ),
              )
            }
            filterName={filterName}
            onChange={onChange}
            fieldLabel={formatMessage({ id: 'schemas.product.productStatus' })}
            value={[value].flat()}
          />
        )}
      </Filter>
    </FilterSection>
  )
}

export default ProductMappingFilters
